import React, { useState, useEffect, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import './QuickBookingModal.css';

const LABELS = {
  ro: {
    title: 'Solicită rezervare',
    category: 'Categorie cameră',
    categoryPlaceholder: 'Selectează categoria',
    checkin: 'Check-in',
    checkout: 'Check-out',
    guests: 'Număr oaspeți',
    name: 'Nume complet',
    phone: 'Telefon',
    submit: 'Solicită rezervare',
    sending: 'Se verifică...',
    success: 'Cerere trimisă! Vă vom contacta în curând pentru confirmare.',
    successSub: 'WhatsApp-ul s-a deschis cu detaliile rezervării.',
    unavailable: 'Ne pare rău, camera nu este disponibilă în intervalul selectat. Vă rugăm alegeți alte date.',
    pendingBookingExists: 'Există deja o cerere de rezervare în așteptare pentru acest număr de telefon. Vă rugăm așteptați confirmarea sau contactați-ne direct.',
    noPrice: 'Nu există tarif definit pentru perioada selectată. Vă rugăm contactați-ne direct.',
    errRequired: 'Toate câmpurile sunt obligatorii.',
    errDates: 'Data de check-out trebuie să fie după check-in.',
    errPhone: 'Introduceți un număr de telefon valid.',
    error: 'Eroare la trimitere. Vă rugăm încercați din nou.',
    close: 'Închide',
    newBooking: 'Altă rezervare',
  },
  en: {
    title: 'Request booking',
    category: 'Room category',
    categoryPlaceholder: 'Select category',
    checkin: 'Check-in',
    checkout: 'Check-out',
    guests: 'Number of guests',
    name: 'Full name',
    phone: 'Phone',
    submit: 'Request booking',
    sending: 'Checking...',
    success: 'Request sent! We will contact you soon for confirmation.',
    successSub: 'WhatsApp has opened with your booking details.',
    unavailable: 'Sorry, the room is not available for the selected dates. Please choose different dates.',
    pendingBookingExists: 'There is already a pending booking request for this phone number. Please wait for confirmation or contact us directly.',
    noPrice: 'No rate defined for the selected period. Please contact us directly.',
    errRequired: 'All fields are required.',
    errDates: 'Check-out must be after check-in.',
    errPhone: 'Please enter a valid phone number.',
    error: 'Submission error. Please try again.',
    close: 'Close',
    newBooking: 'New booking',
  },
  de: {
    title: 'Buchung anfragen',
    category: 'Zimmerkategorie',
    categoryPlaceholder: 'Kategorie wählen',
    checkin: 'Anreise',
    checkout: 'Abreise',
    guests: 'Gästeanzahl',
    name: 'Vollständiger Name',
    phone: 'Telefon',
    submit: 'Buchung anfragen',
    sending: 'Wird geprüft...',
    success: 'Anfrage gesendet! Wir melden uns bald zur Bestätigung.',
    successSub: 'WhatsApp wurde mit Ihren Buchungsdetails geöffnet.',
    unavailable: 'Das Zimmer ist im ausgewählten Zeitraum nicht verfügbar. Bitte wählen Sie andere Daten.',
    pendingBookingExists: 'Für diese Telefonnummer liegt bereits eine ausstehende Buchungsanfrage vor. Bitte warten Sie auf die Bestätigung oder kontaktieren Sie uns direkt.',
    noPrice: 'Für den ausgewählten Zeitraum ist kein Preis definiert. Bitte kontaktieren Sie uns direkt.',
    errRequired: 'Alle Felder sind Pflichtfelder.',
    errDates: 'Check-out muss nach Check-in liegen.',
    errPhone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
    error: 'Fehler beim Senden. Bitte versuchen Sie es erneut.',
    close: 'Schließen',
    newBooking: 'Neue Buchung',
  },
  fr: {
    title: 'Demande de réservation',
    category: 'Catégorie de chambre',
    categoryPlaceholder: 'Sélectionner la catégorie',
    checkin: 'Arrivée',
    checkout: 'Départ',
    guests: "Nombre de personnes",
    name: 'Nom complet',
    phone: 'Téléphone',
    submit: 'Demander une réservation',
    sending: 'Vérification...',
    success: 'Demande envoyée ! Nous vous contacterons bientôt pour confirmation.',
    successSub: 'WhatsApp a été ouvert avec vos détails de réservation.',
    unavailable: "Désolé, la chambre n'est pas disponible pour les dates sélectionnées. Veuillez choisir d'autres dates.",
    pendingBookingExists: 'Une demande de réservation en attente existe déjà pour ce numéro de téléphone. Veuillez attendre la confirmation ou nous contacter directement.',
    noPrice: 'Aucun tarif défini pour la période sélectionnée. Veuillez nous contacter directement.',
    errRequired: 'Tous les champs sont obligatoires.',
    errDates: "Le départ doit être après l'arrivée.",
    errPhone: 'Veuillez entrer un numéro de téléphone valide.',
    error: "Erreur d'envoi. Veuillez réessayer.",
    close: 'Fermer',
    newBooking: 'Nouvelle réservation',
  },
  it: {
    title: 'Richiedi prenotazione',
    category: 'Categoria camera',
    categoryPlaceholder: 'Seleziona categoria',
    checkin: 'Check-in',
    checkout: 'Check-out',
    guests: 'Numero ospiti',
    name: 'Nome completo',
    phone: 'Telefono',
    submit: 'Richiedi prenotazione',
    sending: 'Verifica in corso...',
    success: 'Richiesta inviata! La contatteremo presto per conferma.',
    successSub: 'WhatsApp è stato aperto con i dettagli della prenotazione.',
    unavailable: 'Spiacenti, la camera non è disponibile nelle date selezionate. Scegliere date diverse.',
    pendingBookingExists: 'Esiste già una richiesta di prenotazione in sospeso per questo numero di telefono. Si prega di attendere la conferma o contattarci direttamente.',
    noPrice: 'Nessuna tariffa definita per il periodo selezionato. Contattarci direttamente.',
    errRequired: 'Tutti i campi sono obbligatori.',
    errDates: 'Il check-out deve essere dopo il check-in.',
    errPhone: 'Inserire un numero di telefono valido.',
    error: "Errore di invio. Riprovare.",
    close: 'Chiudi',
    newBooking: 'Nuova prenotazione',
  },
  es: {
    title: 'Solicitar reserva',
    category: 'Categoría de habitación',
    categoryPlaceholder: 'Seleccionar categoría',
    checkin: 'Llegada',
    checkout: 'Salida',
    guests: 'Número de huéspedes',
    name: 'Nombre completo',
    phone: 'Teléfono',
    submit: 'Solicitar reserva',
    sending: 'Verificando...',
    success: 'Solicitud enviada. Nos pondremos en contacto pronto para confirmación.',
    successSub: 'WhatsApp se ha abierto con los detalles de su reserva.',
    unavailable: 'Lo sentimos, la habitación no está disponible para las fechas seleccionadas. Por favor, elija otras fechas.',
    pendingBookingExists: 'Ya existe una solicitud de reserva pendiente para este número de teléfono. Por favor, espere la confirmación o contáctenos directamente.',
    noPrice: 'No hay tarifa definida para el período seleccionado. Contáctenos directamente.',
    errRequired: 'Todos los campos son obligatorios.',
    errDates: 'La salida debe ser posterior a la llegada.',
    errPhone: 'Por favor, introduzca un número de teléfono válido.',
    error: 'Error de envío. Por favor, inténtelo de nuevo.',
    close: 'Cerrar',
    newBooking: 'Nueva reserva',
  },
};

const WA_LABELS = {
  ro: { greeting: 'Buna ziua! As dori o rezervare la *Eden Costinești*.', room: 'Categorie', checkin: 'Check-in', checkout: 'Check-out', guests: 'Oaspeți', name: 'Nume', phone: 'Telefon' },
  en: { greeting: 'Hello! I would like to book a room at *Eden Costinești*.', room: 'Category', checkin: 'Check-in', checkout: 'Check-out', guests: 'Guests', name: 'Name', phone: 'Phone' },
  de: { greeting: 'Guten Tag! Ich möchte ein Zimmer in *Eden Costinești* buchen.', room: 'Kategorie', checkin: 'Anreise', checkout: 'Abreise', guests: 'Gäste', name: 'Name', phone: 'Telefon' },
  fr: { greeting: 'Bonjour ! Je souhaite réserver une chambre à *Eden Costinești*.', room: 'Catégorie', checkin: 'Arrivée', checkout: 'Départ', guests: 'Personnes', name: 'Nom', phone: 'Téléphone' },
  it: { greeting: 'Buongiorno! Desidero prenotare una camera a *Eden Costinești*.', room: 'Categoria', checkin: 'Check-in', checkout: 'Check-out', guests: 'Ospiti', name: 'Nome', phone: 'Telefono' },
  es: { greeting: 'Buenos días! Me gustaría reservar una habitación en *Eden Costinești*.', room: 'Categoría', checkin: 'Llegada', checkout: 'Salida', guests: 'Huéspedes', name: 'Nombre', phone: 'Teléfono' },
};

function fmt(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDisplay(d) {
  if (!d) return '-';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

export default function QuickBookingModal({ isOpen, onClose, mode = 'booking' }) {
  const { activeLang, t } = useLanguage();
  const l = LABELS[activeLang] || LABELS.ro;
  const overlayRef = useRef(null);
  const enableAvailabilityCheck = t('enable_availability_check', 'true') !== 'false';

  const [categories, setCategories] = useState([]);
  const [categorySlug, setCategorySlug] = useState('');
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'wa_sending' | 'wa_sent' | 'unavailable' | 'noprice' | 'error'

  useEffect(() => {
    if (isOpen) {
      api.get('/rooms', { params: { lang: activeLang } })
        .then(res => setCategories(res.data || []))
        .catch(() => setCategories([]));
    }
  }, [isOpen, activeLang]);

  const handleClose = useCallback(() => {
    setCategorySlug('');
    setCheckIn(null);
    setCheckOut(null);
    setGuests(2);
    setName('');
    setPhone('');
    setError('');
    setStatus(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) handleClose();
  }

  function validateFields() {
    if (!categorySlug || !checkIn || !checkOut || !name.trim() || !phone.trim()) {
      setError(l.errRequired);
      return null;
    }
    if (checkOut <= checkIn) {
      setError(l.errDates);
      return null;
    }
    const cleanPhone = phone.replace(/[\s\-().]/g, '');
    const normalizedPhone = /^[0-9]{9}$/.test(cleanPhone) ? '0' + cleanPhone : cleanPhone;
    if (!/^\+?[0-9]{10,15}$/.test(normalizedPhone)) {
      setError(l.errPhone);
      return null;
    }
    return normalizedPhone;
  }

  function buildWaUrl(cleanPhone, catName) {
    const waPhone = t('contact_phone', '').replace(/\D/g, '');
    if (!waPhone) return null;
    const w = WA_LABELS[activeLang] || WA_LABELS.ro;
    const lines = [
      w.greeting,
      '',
      `${w.room}: ${catName}`,
      `${w.checkin}: ${fmtDisplay(checkIn)}`,
      `${w.checkout}: ${fmtDisplay(checkOut)}`,
      `${w.guests}: ${guests}`,
      '',
      `${w.name}: ${name.trim()}`,
      `${w.phone}: ${cleanPhone}`,
    ];
    return `https://wa.me/${waPhone}?text=${encodeURIComponent(lines.join('\n'))}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setStatus(null);

    const cleanPhone = validateFields();
    if (!cleanPhone) return;

    setStatus('sending');

    try {
      const availRes = await api.get(`/rooms/${categorySlug}/availability`, {
        params: { check_in: fmt(checkIn), check_out: fmt(checkOut), phone: cleanPhone },
      });
      const availableRooms = availRes.data?.available_rooms || [];
      if (!availableRooms.length) {
        setStatus('unavailable');
        return;
      }
      const roomId = availableRooms[0].id;

      await api.post('/bookings', {
        room_id: roomId,
        check_in: fmt(checkIn),
        check_out: fmt(checkOut),
        guests_count: guests,
        guest_name: name.trim(),
        phone: cleanPhone,
      });

      setStatus('success');
    } catch (err) {
      const errCode = err.response?.data?.error;
      if (errCode === 'no_price_defined') {
        setStatus('noprice');
      } else if (errCode === 'pending_booking_exists') {
        setStatus('pending_booking_exists');
      } else if (err.response?.status === 409) {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }
    }
  }

  async function handleWhatsAppClick() {
    setError('');
    setStatus(null);

    const cleanPhone = validateFields();
    if (!cleanPhone) return;

    const catName = categories.find(c => c.slug === categorySlug)?.name || categorySlug;
    const waUrl = buildWaUrl(cleanPhone, catName);

    if (!enableAvailabilityCheck) {
      // Skip availability check: compose and open WA directly
      if (waUrl) {
        window.open(waUrl, '_blank', 'noopener,noreferrer');
        setStatus('wa_sent');
      }
      return;
    }

    // Open the window synchronously (during the click event) so mobile browsers
    // don't block it as a popup. We'll navigate it to the WhatsApp URL after
    // the async availability check completes.
    const waWindow = window.open('', '_blank');

    setStatus('wa_sending');

    try {
      const availRes = await api.get(`/rooms/${categorySlug}/availability`, {
        params: { check_in: fmt(checkIn), check_out: fmt(checkOut), phone: cleanPhone },
      });
      const availableRooms = availRes.data?.available_rooms || [];
      if (!availableRooms.length) {
        if (waWindow) waWindow.close();
        setStatus('unavailable');
        return;
      }
      const catName = categories.find(c => c.slug === categorySlug)?.name || categorySlug;
      const waUrl = buildWaUrl(cleanPhone, catName);
      if (waUrl && waWindow) {
        waWindow.location.href = waUrl;
      } else if (waWindow) {
        waWindow.close();
      }
      setStatus('wa_sent');
    } catch (err) {
      if (waWindow) waWindow.close();
      const errCode = err.response?.data?.error;
      if (errCode === 'no_price_defined') {
        setStatus('noprice');
      } else if (errCode === 'pending_booking_exists') {
        setStatus('pending_booking_exists');
      } else if (err.response?.status === 409) {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }
    }
  }

  if (!isOpen) return null;

  const isDone = status === 'success' || status === 'wa_sent';
  const isBusy = status === 'sending' || status === 'wa_sending';
  const isError = error || status === 'unavailable' || status === 'noprice' || status === 'error' || status === 'pending_booking_exists';
  const errorMsg = error
    || (status === 'unavailable' ? l.unavailable : null)
    || (status === 'pending_booking_exists' ? l.pendingBookingExists : null)
    || (status === 'noprice' ? l.noPrice : null)
    || (status === 'error' ? l.error : null);

  return (
    <div className="qbm-overlay" ref={overlayRef} onClick={handleOverlayClick} role="presentation">
      <div className="qbm-modal" role="dialog" aria-modal="true" aria-labelledby="qbm-title">
        <button className="qbm-close" onClick={handleClose} aria-label={l.close}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="18" height="18" aria-hidden="true">
            <path strokeLinecap="round" d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="qbm-header">
          <div className="qbm-header__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
              <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" strokeLinecap="round" strokeWidth="2.4" />
            </svg>
          </div>
          <h2 className="qbm-title" id="qbm-title">{l.title}</h2>
        </div>

        <form className="qbm-form" onSubmit={handleSubmit} noValidate>
            <div className="qbm-field">
              <label className="qbm-label" htmlFor="qbm-category">{l.category}</label>
              <select
                id="qbm-category"
                className="qbm-select"
                value={categorySlug}
                onChange={e => { setCategorySlug(e.target.value); setError(''); setStatus(null); }}
                disabled={isBusy || isDone}
              >
                <option value="">{l.categoryPlaceholder}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="qbm-row">
              <div className="qbm-field">
                <label className="qbm-label">{l.checkin}</label>
                <DatePicker
                  selected={checkIn}
                  onChange={d => { setCheckIn(d); setError(''); setStatus(null); }}
                  selectsStart
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={new Date()}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="zz-ll-aaaa"
                  disabled={isBusy || isDone}
                  className="qbm-datepicker"
                  wrapperClassName="qbm-datepicker-wrapper"
                />
              </div>
              <div className="qbm-field">
                <label className="qbm-label">{l.checkout}</label>
                <DatePicker
                  selected={checkOut}
                  onChange={d => { setCheckOut(d); setError(''); setStatus(null); }}
                  selectsEnd
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={checkIn || new Date()}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="zz-ll-aaaa"
                  disabled={isBusy || isDone}
                  className="qbm-datepicker"
                  wrapperClassName="qbm-datepicker-wrapper"
                />
              </div>
            </div>

            <div className="qbm-field qbm-field--guests">
              <label className="qbm-label" htmlFor="qbm-guests">{l.guests}</label>
              <div className="qbm-guests-ctrl">
                <button
                  type="button"
                  className="qbm-guests-btn"
                  onClick={() => setGuests(g => Math.max(1, g - 1))}
                  disabled={isBusy || isDone || guests <= 1}
                  aria-label="Scade"
                >−</button>
                <input
                  id="qbm-guests"
                  type="number"
                  className="qbm-guests-input"
                  min={1}
                  max={20}
                  value={guests}
                  onChange={e => setGuests(Math.min(20, Math.max(1, Number(e.target.value))))}
                  disabled={isBusy || isDone}
                />
                <button
                  type="button"
                  className="qbm-guests-btn"
                  onClick={() => setGuests(g => Math.min(20, g + 1))}
                  disabled={isBusy || isDone || guests >= 20}
                  aria-label="Adaugă"
                >+</button>
              </div>
            </div>

            <div className="qbm-row">
              <div className="qbm-field">
                <label className="qbm-label" htmlFor="qbm-name">{l.name}</label>
                <input
                  id="qbm-name"
                  type="text"
                  className="qbm-input"
                  value={name}
                  onChange={e => { setName(e.target.value); setError(''); }}
                  disabled={isBusy || isDone}
                  autoComplete="name"
                  placeholder=""
                />
              </div>
              <div className="qbm-field">
                <label className="qbm-label" htmlFor="qbm-phone">{l.phone}</label>
                <input
                  id="qbm-phone"
                  type="tel"
                  className="qbm-input"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  disabled={isBusy || isDone}
                  autoComplete="tel"
                  placeholder=""
                />
              </div>
            </div>

            {isError && (
              <div className="qbm-error" role="alert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" d="M12 8v4M12 16h.01" strokeWidth="2.4" />
                </svg>
                {errorMsg}
              </div>
            )}

            {status === 'success' ? (
              <>
                <div className="qbm-success-inline" role="status">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 12.5l3 3 6-6" />
                  </svg>
                  {l.success}
                </div>
                <button type="button" className="qbm-btn qbm-btn--primary qbm-btn--full" onClick={handleClose}>
                  {l.close}
                </button>
              </>
            ) : status === 'wa_sent' ? (
              <>
                <div className="qbm-success-inline" role="status">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 12.5l3 3 6-6" />
                  </svg>
                  {l.successSub}
                </div>
                <button type="button" className="qbm-btn qbm-btn--primary qbm-btn--full" onClick={handleClose}>
                  {l.close}
                </button>
              </>
            ) : mode === 'whatsapp' ? (
              <button
                type="button"
                className="qbm-btn qbm-btn--whatsapp qbm-btn--full"
                onClick={handleWhatsAppClick}
                disabled={isBusy}
              >
                {status === 'wa_sending' ? (
                  <><span className="qbm-spinner" aria-hidden="true" />{l.sending}</>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="17" height="17" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    WhatsApp
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                className="qbm-btn qbm-btn--primary qbm-btn--full"
                disabled={isBusy}
              >
                {status === 'sending' ? (
                  <><span className="qbm-spinner" aria-hidden="true" />{l.sending}</>
                ) : (
                  l.submit
                )}
              </button>
            )}
          </form>
      </div>
    </div>
  );
}
